/**
 * Breaking news and Public records: types and Brian's catalogues
 * (app-data.js WATCH_TYPES and RECORD_TYPES, v4 / v5.6). Kept here rather than
 * in types/database.ts so this area can change without touching shared files.
 */

/** The types a watch can be on (watch spec §6): nine national topics, and the
 *  two local ones (Safety, Weather) where a place has a feed. Ids follow our
 *  topic taxonomy; aviation has no feed topic of its own. */
export const WATCH_TYPES = {
  'celebrity-news': { name: 'Celebrity News', scope: 'national', conditions: ['pregnancy', 'engagement', 'breakup', 'injury', 'arrest', 'death'] },
  'artists-bands': { name: 'Artists & Bands', scope: 'national', conditions: ['tour dates', 'new album', 'breakup', 'cancellation', 'death'] },
  'reality-tv': { name: 'Reality TV', scope: 'national', conditions: ['casting', 'cancellation', 'exit', 'arrest'] },
  'true-crime': { name: 'True Crime', scope: 'national', conditions: ['arrest', 'verdict', 'sentencing', 'new evidence'] },
  'technology': { name: 'Technology', scope: 'national', conditions: ['layoffs', 'product launch', 'outage', 'acquisition'] },
  'space': { name: 'Space', scope: 'national', conditions: ['launch', 'launch failure', 'landing'] },
  'aviation': { name: 'Aviation', scope: 'national', conditions: ['crash', 'grounding', 'new route'] },
  'cars': { name: 'Cars', scope: 'national', conditions: ['recall', 'new model', 'plant closure'] },
  'pro-sports': { name: 'Pro Sports', scope: 'national', conditions: ['injury', 'trade', 'signing', 'suspension'] },
  'safety-alerts': { name: 'Safety Alerts', scope: 'local', conditions: ['major incident', 'structure fire', 'road closure', 'evacuation'] },
  'snow-weather': { name: 'Snow & Weather', scope: 'local', conditions: ['winter storm warning', 'red flag warning', 'flood warning'] },
} as const satisfies Record<string, { name: string; scope: 'national' | 'local'; conditions: readonly string[] }>

export type WatchTypeId = keyof typeof WATCH_TYPES

export interface Watch { id: string; type: WatchTypeId; subject: string; condition: string; own: boolean }

export interface WatchState {
  bank: number
  cap: number
  paused: boolean
  auto: boolean
  floor: number
  push: boolean
  types: WatchTypeId[]
  step: number
  /** The account follows a city or county, so Safety and Weather can be watched. */
  local: boolean
  watches: Watch[]
  /** Fills and top-ups, newest first: "Topped up to 30 · Oct 3". */
  history: { kind: 'fill' | 'topup'; n: number; bank: number; at: string }[]
  /** How many − can move back this week (only what was moved in this week). */
  movable_out: number
  last_scan_at: string | null
}

export interface Alert {
  id: string
  client_id: string
  watch_id: string | null
  type: string
  subject: string
  condition: string
  headline: string
  dek: string | null
  outlet: string
  source_url: string | null
  broke_at: string
  delivered_at: string
  status: 'new' | 'built' | 'dismissed'
  done_at: string | null
  headline_id: string | null
  story_id: string | null
  test: boolean
}

// ---------------- Public records ----------------

export const RECORD_TYPES = {
  liquor: { name: 'Liquor licenses', topic: 'food-drink', desc: 'A business applying to sell or serve alcohol: usually the first public sign a restaurant, bar or shop is coming.' },
  business: { name: 'Business licenses', topic: 'new-business-openings', desc: 'A new business registering in town, with a street address.' },
  permit: { name: 'Permits', topic: 'development-construction', desc: 'New buildings, commercial build-outs, demolitions and land-use applications. Never a homeowner’s remodel.' },
  agenda: { name: 'Meeting agendas', topic: 'civic-government', desc: 'The coming week’s council, planning and county agendas: the items worth a story and when they’re voted on. The next check says what was decided.' },
  release: { name: 'Data releases', topic: 'finance', desc: 'The scheduled housing and economic releases. Release days are checked the morning they land.' },
} as const

export type RecordTypeId = keyof typeof RECORD_TYPES

export interface RecordSource {
  id: string
  type: RecordTypeId
  place: string
  scope: 'place' | 'county' | 'state' | 'national'
  name: string
  url: string | null
  status: 'checking' | 'unreachable' | 'unavailable'
  on: boolean
  last_checked_at: string | null
  last_tried_at: string | null
  next_release: string | null
  unreachable_weeks: number
  follows: boolean
}

export interface RecordCheck { id: string; kind: string; at: string; checked: number; unreachable: number; skipped: number; found: number; charged: number }

export interface RecordsState {
  paused: boolean
  push: boolean
  types: RecordTypeId[]
  cap: number
  spent: number
  left: number
  /** Sources on and checking weekly. */
  on: number
  over_budget: number
  next_check: string
  checked_this_week: boolean | null
  national: boolean
  local: boolean
  places: { name: string; scope: string; home: boolean }[]
  sources: RecordSource[]
  last_check: RecordCheck | null
}

export interface RecordFind {
  id: string
  source_id: string | null
  type: RecordTypeId
  place: string
  title: string
  facts: string[]
  next_what: string | null
  next_date: string | null
  doc_name: string
  doc_url: string | null
  doc_filed: string | null
  doc_checked: string
  source_name: string | null
  follows_up: string | null
  decided: boolean
  release: boolean
  status: 'new' | 'built' | 'dismissed'
  done_at: string | null
  story_id: string | null
  created_at: string
}
